import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { AppQueueContentType } from "@packages/sdk";
import { useRedirectToAuth, useSession } from "@packages/sdk";
import type { MouseEvent, MouseEventHandler } from "react";
import { useCallback } from "react";

import { usePlayer } from "./usePlayer";

export type UseAddToQueueProps = {
  type: AppQueueContentType;
  id: number;
};

export type UseAddToQueueReturn = {
  handleClickNext: MouseEventHandler<HTMLDivElement | HTMLButtonElement>;
  handleClickLast: MouseEventHandler<HTMLDivElement | HTMLButtonElement>;
  stringKeyNext: string;
  stringKeyLast: string;
};

export const useAddToQueue = ({
  type,
  id,
}: UseAddToQueueProps): UseAddToQueueReturn => {
  const player = usePlayer();
  const { track } = useAnalytics();
  const { status } = useSession();
  const redirectToAuth = useRedirectToAuth();

  const trackOption = useCallback(
    (option: "play_next" | "play_last") => {
      if (["collection", "collection-next", "prayer"].includes(type)) {
        if (type === "collection" || type === "collection-next") {
          void track({
            event: HallowAnalyticsEvent.TappedCollectionOption,
            properties: {
              collection: id,
              option,
            },
          });
        } else {
          void track({
            event: HallowAnalyticsEvent.TappedPrayerOption,
            properties: {
              prayer: id,
              option,
            },
          });
        }
      }
    },
    [track, id, type],
  );

  const handleClickNext = useCallback(
    (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      e.stopPropagation();
      trackOption("play_next");
      if (status === "authenticated")
        void player?.addToQueue({ type, id, position: "next" });
      else void redirectToAuth();
    },
    [player, status, type, id, trackOption],
  );

  const handleClickLast = useCallback(
    (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      e.stopPropagation();
      trackOption("play_last");
      if (status === "authenticated")
        void player?.addToQueue({ type, id, position: "last" });
      else void redirectToAuth();
    },
    [player, status, type, id, trackOption],
  );

  let stringKeyNext = "play_next";
  let stringKeyLast = "add_to_queue";
  if (type === "collection") {
    stringKeyNext = "play_all_next";
    stringKeyLast = "play_all_later";
  }

  return { handleClickLast, handleClickNext, stringKeyNext, stringKeyLast };
};
